import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Container } from '../../components/layout/Container'
import { Card } from '../../components/ui/Card'
import { PlatformHero } from '../../components/sections/platform/PlatformHero'
import { ModuleOverview } from '../../components/sections/platform/ModuleOverview'
import { FeatureGrid } from '../../components/sections/platform/FeatureGrid'
import { MetricsSection } from '../../components/sections/platform/MetricsSection'
import { ModuleFAQ } from '../../components/sections/platform/ModuleFAQ'
import { PlatformCTA } from '../../components/sections/platform/PlatformCTA'
import { ModuleNav } from '../../components/sections/platform/ModuleNav'
import { useScrollAnimation } from '../../hooks/useScrollAnimation'
import { CheckCircle2, XCircle, Clock } from 'lucide-react'

interface ExecutionItem {
  id: string
  name: string
  status: 'passed' | 'blocked' | 'running'
  time: string
}

const statusConfig = {
  passed: { icon: CheckCircle2, label: 'passed', color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
  blocked: { icon: XCircle, label: 'blocked', color: 'text-error', bg: 'bg-error/10' },
  running: { icon: Clock, label: 'running', color: 'text-warning', bg: 'bg-warning/10' },
}

function ExecutionStatusSection() {
  const { t } = useTranslation()
  const { ref, isInView } = useScrollAnimation()

  const items = t('platformPages.govern.executions.items', { returnObjects: true }) as ExecutionItem[]

  return (
    <section ref={ref} className="border-y border-border py-24">
      <Container size="wide">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <span className="text-sm font-medium uppercase tracking-wider text-accent-light">
            {t('platformPages.govern.executions.label')}
          </span>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-text-primary sm:text-4xl">
            {t('platformPages.govern.executions.title')}
          </h2>
        </motion.div>

        <div className="mt-12">
          <Card variant="elevated" className="overflow-hidden p-0">
            <div className="grid grid-cols-12 gap-4 border-b border-border px-6 py-4 text-xs font-medium uppercase tracking-wider text-text-muted">
              <div className="col-span-6 sm:col-span-5">{t('platformPages.govern.executions.agent')}</div>
              <div className="col-span-3 sm:col-span-3">{t('platformPages.govern.executions.status')}</div>
              <div className="col-span-3 hidden text-right sm:col-span-4 sm:block">{t('platformPages.govern.executions.time')}</div>
            </div>
            {items.map((item, index) => {
              const config = statusConfig[item.status]
              const Icon = config.icon
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.3, delay: index * 0.08 }}
                  className="grid grid-cols-12 items-center gap-4 border-b border-border/50 px-6 py-4 last:border-b-0"
                >
                  <div className="col-span-6 font-medium text-text-primary sm:col-span-5">{item.name}</div>
                  <div className="col-span-3">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${config.bg} ${config.color}`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {t(`platformPages.govern.executions.${config.label}`)}
                    </span>
                  </div>
                  <div className="col-span-3 hidden text-right text-sm text-text-secondary sm:col-span-4 sm:block">
                    {item.time}
                  </div>
                </motion.div>
              )
            })}
          </Card>
        </div>
      </Container>
    </section>
  )
}

export function GovernPage() {
  return (
    <>
      <PlatformHero tKey="govern" />
      <ModuleOverview tKey="govern" />
      <FeatureGrid tKey="govern" />
      <ExecutionStatusSection />
      <MetricsSection tKey="govern" />
      <ModuleFAQ tKey="govern" />
      <PlatformCTA />
      <ModuleNav />
    </>
  )
}
